import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { z } from 'zod';
import {
  customerSorts,
  customerViews,
  salesMetrics,
  type CustomerFilterSet,
  type CustomerSort,
  type SalesMetric,
  type SortDir,
} from '@paige/admin';

/**
 * Everything the Customers page shows is in the URL, so a search, a filter, an order or a page is a link to
 * share: `?q=&plan=&paying=&pastDue=&test=&problems=&view=&partner=&sort=&dir=&page=&show=`. The period's
 * three params beside them are the admin area's own (period-search.ts). A value that is not one never
 * fails the route: it is dropped.
 */

/** Text from the URL: the router reads `?q=1007` as a number, and a company can be called that. */
const text = (max: number, pattern?: RegExp) =>
  z
    .preprocess(
      (v) => (typeof v === 'number' ? String(v) : v),
      pattern ? z.string().max(max).regex(pattern) : z.string().max(max),
    )
    .optional()
    .catch(undefined);
const on = z.boolean().optional().catch(undefined);

export const customersSearch = {
  q: text(80),
  plan: text(40),
  paying: on,
  pastDue: on,
  test: on,
  problems: on,
  view: z.enum(customerViews).optional().catch(undefined),
  partner: text(4, /^([A-Za-z2-9]{4}|none)$/),
  sort: z.enum(customerSorts).optional().catch(undefined),
  dir: z.enum(['asc', 'desc']).optional().catch(undefined),
  page: z.number().int().min(1).max(100_000).optional().catch(undefined),
  /** The series the sales picture draws. */
  show: z.enum(salesMetrics).optional().catch(undefined),
};

const schema = z.object(customersSearch);
export type CustomersSearch = z.infer<typeof schema>;

/** What the page shows, read from the URL with every default filled in. */
export interface CustomersView {
  filters: CustomerFilterSet;
  sort: CustomerSort;
  dir: SortDir;
  /** 1 for the first page. */
  page: number;
  metric: SalesMetric;
}

export const DEFAULT_SORT: CustomerSort = 'paid';

export function readSearch(s: CustomersSearch): CustomersView {
  const sort = s.sort ?? DEFAULT_SORT;
  return {
    filters: {
      q: s.q ?? '',
      plan: s.plan ?? null,
      paying: s.paying === true,
      pastDue: s.pastDue === true,
      test: s.test === true,
      problems: s.problems === true,
      view: s.view ?? 'all',
      partner: s.partner ?? null,
    },
    sort,
    dir: s.dir ?? (sort === 'name' ? 'asc' : 'desc'),
    page: s.page ?? 1,
    metric: s.show ?? 'revenue',
  };
}

/** The URL for a view: only what differs from the defaults, so the plain page has a plain address. */
export function writeSearch(v: CustomersView): CustomersSearch {
  const f = v.filters;
  const all: Record<keyof CustomersSearch, unknown> = {
    q: only(f.q !== '', f.q),
    plan: only(f.plan !== null, f.plan),
    paying: only(f.paying, true),
    pastDue: only(f.pastDue, true),
    test: only(f.test, true),
    problems: only(f.problems, true),
    view: only(f.view !== 'all', f.view),
    partner: only(f.partner !== null, f.partner),
    sort: only(v.sort !== DEFAULT_SORT, v.sort),
    dir: only(v.dir !== firstDirOf(v.sort), v.dir),
    page: only(v.page > 1, v.page),
    show: only(v.metric !== 'revenue', v.metric),
  };
  return Object.fromEntries(Object.entries(all).filter(([, value]) => value !== undefined));
}

/** The value when it differs from the default, else nothing to write. */
function only<T>(differs: boolean, value: T): T | undefined {
  return differs ? value : undefined;
}

const firstDirOf = (sort: CustomerSort): SortDir => (sort === 'name' ? 'asc' : 'desc');

/** A stored search (a staff member's own view) read back safely: anything that is not a value is dropped. */
export function parseSearch(stored: unknown): CustomersSearch {
  const parsed = schema.safeParse(stored);
  return parsed.success ? parsed.data : {};
}

export interface CustomersUrl extends CustomersView {
  /** Changes filters; the list goes back to its first page. */
  setFilters: (change: Partial<CustomerFilterSet>) => void;
  setSort: (sort: CustomerSort, dir: SortDir) => void;
  setPage: (page: number) => void;
  setMetric: (metric: SalesMetric) => void;
  /** Replaces the whole view, and the period with it when one is given. */
  replace: (search: CustomersSearch, period?: Record<string, string | undefined>) => void;
}

type Loose = Record<string, unknown>;

/** The period's own params, kept across every change this page makes. */
function periodOf(s: Loose): Loose {
  const kept: Loose = {};
  for (const key of ['period', 'from', 'until']) if (s[key] !== undefined) kept[key] = s[key];
  return kept;
}

/** The page's view state in the URL. */
export function useCustomersUrl(): CustomersUrl {
  const search = useSearch({ from: '/admin/' });
  const navigate = useNavigate({ from: '/admin/' });
  const view = useMemo(() => readSearch(search), [search]);
  const go = useCallback(
    (next: CustomersView) =>
      void navigate({
        replace: true,
        search: (s: Loose) => ({ ...periodOf(s), ...writeSearch(next) }),
      } as never),
    [navigate],
  );
  const replace = useCallback<CustomersUrl['replace']>(
    (stored, period) =>
      void navigate({
        replace: true,
        search: (s: Loose) => ({ ...(period ?? periodOf(s)), ...stored }),
      } as never),
    [navigate],
  );
  return {
    ...view,
    setFilters: (change) => go({ ...view, filters: { ...view.filters, ...change }, page: 1 }),
    setSort: (sort, dir) => go({ ...view, sort, dir, page: 1 }),
    setPage: (page) => go({ ...view, page }),
    setMetric: (metric) => go({ ...view, metric }),
    replace,
  };
}

/** When the period changes under a later page, the list goes back to its first: page 7 of another month is nothing. */
export function useFirstPageOnPeriod(url: CustomersUrl, from: string, until: string): void {
  const last = useRef(`${from}/${until}`);
  const { page, setPage } = url;
  useEffect(() => {
    const now = `${from}/${until}`;
    if (now === last.current) return;
    last.current = now;
    if (page > 1) setPage(1);
  }, [from, until, page, setPage]);
}
